import { useAppType } from '@dealstack/frontend/shared/mechanism/appTypeContext/appTypeContext';
import {
	loadingTranslationsError,
	loadingTranslationsStart,
	loadingTranslationsSuccess,
} from '@dealstack/frontend/shared/mechanism/redux/reducers/global/global';
import { PROJECT } from '@dealstack/shared/lib/common/environment';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { Languages } from '@dealstack/shared/lib/common/i18n/i18n';
import { useGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { useFeatureFlag } from 'configcat-react';
import i18n from 'i18next';
import React, { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setupI18nAsync } from './i18nSetup';
import { StorageKeys, StorageUtilities } from '@dealstack/frontend/shared/helper/storageUtils';

interface Props {
	children: React.ReactNode;
}

export const DEFAULT_LANGUAGE = Languages.EN;
export const I18NLayer = ({ children }: Props) => {
	const appType = useAppType();
	const UILanguageListsConfig = useFeatureFlag(FEATURE_FLAG.UI_LANGUAGE_LIST, DEFAULT_LANGUAGE);

	const usesLanguageFlag = appType === PROJECT.INVESTOR;
	const awaitingLanguageFlag = usesLanguageFlag && UILanguageListsConfig.loading;

	const languageList: string[] =
		usesLanguageFlag && UILanguageListsConfig.value
			? UILanguageListsConfig.value.split(',')
			: [DEFAULT_LANGUAGE];

	const globalConfig = useGlobalConfig();
	const [i18nInstance, setI18nInstance] = useState<null | Awaited<
		ReturnType<typeof setupI18nAsync>
	>>(null);
	const dispatch = useDispatch();

	useEffect(() => {
		if (!globalConfig.apiUrl || awaitingLanguageFlag) {
			return;
		}
		dispatch(loadingTranslationsStart());

		void (async () => {
			try {
				StorageUtilities.setItem(StorageKeys.UI_LANGUAGE_LIST, languageList);
				const i18n = await setupI18nAsync({
					commonApiUrl: globalConfig.apiUrl,
					tolgeeApiUrl: globalConfig.tolgeeApiUrl,
					tolgeeApiKey: globalConfig.tolgeeApiKey,
					languageList,
				});
				setI18nInstance(i18n);
				dispatch(loadingTranslationsSuccess());
			} catch (error) {
				dispatch(loadingTranslationsError(error));
			}
		})();
	}, [awaitingLanguageFlag]);

	return <I18nextProvider i18n={i18nInstance as typeof i18n}>{children}</I18nextProvider>;
};
